/**
 * useNews -- Zustand store for the unified news aggregation page.
 * Auto-refreshes every 2 minutes.
 */
import { create } from 'zustand'
import { getNewsFeed, getBreakingNews, getSecFilings } from '@/lib/api'
import type { NewsFeedItem, SecFilingItem } from '@/lib/types'
import { useEffect } from 'react'

/** Extract a human-readable message from an Axios-ish error. Exported for tests. */
export function newsErrorMessage(e: unknown): string {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown } | null
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (typeof err?.message === 'string' && err.message) return err.message
  return String(e)
}

interface NewsState {
  items: NewsFeedItem[]
  breaking: NewsFeedItem[]
  secFilings: SecFilingItem[]
  secFilingsLoading: boolean
  secFilingsError: string | null
  loading: boolean
  error: string | null
  lastFetched: Date | null
  fetch: () => Promise<void>
  fetchSecFilings: (days?: number, formType?: string) => Promise<void>
}

export const useNewsStore = create<NewsState>()((set) => ({
  items: [],
  breaking: [],
  secFilings: [],
  secFilingsLoading: false,
  secFilingsError: null,
  loading: false,
  error: null,
  lastFetched: null,

  fetch: async () => {
    set({ loading: true, error: null })
    // allSettled never throws, so failures must be read off the rejected
    // results — the old catch block was unreachable and errors were silent.
    const [feedRes, breakingRes] = await Promise.allSettled([
      getNewsFeed(80),
      getBreakingNews(),
    ])

    const items = feedRes.status === 'fulfilled' ? feedRes.value.items ?? [] : []
    const breaking = breakingRes.status === 'fulfilled' ? breakingRes.value.items ?? [] : []

    let error: string | null = null
    if (feedRes.status === 'rejected' && breakingRes.status === 'rejected') {
      error = `News feeds failed to load: ${newsErrorMessage(feedRes.reason)}`
    } else if (feedRes.status === 'rejected') {
      error = `News feed failed to load: ${newsErrorMessage(feedRes.reason)}`
    } else if (breakingRes.status === 'rejected') {
      error = `Breaking news failed to load: ${newsErrorMessage(breakingRes.reason)}`
    }

    set({ items, breaking, loading: false, lastFetched: new Date(), error })
  },

  fetchSecFilings: async (days = 7, formType = 'all') => {
    set({ secFilingsLoading: true, secFilingsError: null })
    try {
      const res = await getSecFilings(days, formType)
      set({ secFilings: res.filings ?? [], secFilingsLoading: false })
    } catch (e) {
      // Surface the failure — an empty table with "No filings found" is a lie
      // when the fetch itself failed.
      set({ secFilingsLoading: false, secFilingsError: newsErrorMessage(e) })
    }
  },
}))

/** Drop-in hook: fetches on mount and auto-refreshes every 2 minutes. */
export function useNews(intervalMs = 120_000) {
  const {
    fetch,
    fetchSecFilings,
    items,
    breaking,
    secFilings,
    secFilingsLoading,
    secFilingsError,
    loading,
    error,
    lastFetched,
  } = useNewsStore()

  useEffect(() => {
    fetch()
    const id = setInterval(fetch, intervalMs)
    return () => clearInterval(id)
  }, [fetch, intervalMs])

  return {
    items,
    breaking,
    secFilings,
    secFilingsLoading,
    secFilingsError,
    loading,
    error,
    lastFetched,
    refetch: fetch,
    fetchSecFilings,
  }
}
